import React, { useLayoutEffect, useRef, useState } from "react";
import KuikaCheckbox from "../../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { noop } from "../_layout/utils";

interface IColorSwatch {
  token: string;
  hex: string;
}

interface IColorScale {
  name: string;
  swatches: IColorSwatch[];
}

const NEUTRAL_SCALE: IColorScale = {
  name: "Neutral / Dark",
  swatches: [
    { token: "--white", hex: "#ffffff" },
    { token: "--black", hex: "#17253c" },
    { token: "--dark", hex: "#17253c" },
    { token: "--dark-light", hex: "#44546f" },
    { token: "--dark-0", hex: "#ffffff" },
    { token: "--dark-1", hex: "#f7f8f9" },
    { token: "--dark-2", hex: "#f1f2f4" },
    { token: "--dark-3", hex: "#dcdfe4" },
    { token: "--dark-4", hex: "#b2b9c3" },
    { token: "--dark-5", hex: "#8490a1" },
    { token: "--dark-6", hex: "#6e7e96" },
    { token: "--dark-7", hex: "#606f85" },
    { token: "--dark-8", hex: "#41546e" },
    { token: "--dark-9", hex: "#293f5b" },
    { token: "--dark-10", hex: "#122c4b" },
    { token: "--dark-11", hex: "#17253c" }
  ]
};

const HUE_SCALES: IColorScale[] = [
  {
    name: "Blue",
    swatches: [
      { token: "--blue-05", hex: "#f2f7ff" },
      { token: "--blue-1", hex: "#e5eeff" },
      { token: "--blue-2", hex: "#ccddff" },
      { token: "--blue-3", hex: "#b2ccff" },
      { token: "--blue-4", hex: "#4d88ff" },
      { token: "--blue-5", hex: "#246dff" },
      { token: "--blue-6", hex: "#2062e5" },
      { token: "--blue-7", hex: "#164199" },
      { token: "--blue-8", hex: "#002369" },
      { token: "--blue-9", hex: "#192640" }
    ]
  },
  {
    name: "Red",
    swatches: [
      { token: "--red-1", hex: "#ffebeb" },
      { token: "--red-2", hex: "#ffd3d2" },
      { token: "--red-3", hex: "#ff9492" },
      { token: "--red-4", hex: "#fd6d6a" },
      { token: "--red-5", hex: "#e04441" },
      { token: "--red-6", hex: "#d12d2a" },
      { token: "--red-7", hex: "#a01d1b" },
      { token: "--red-8", hex: "#560d0c" }
    ]
  },
  {
    name: "Green",
    swatches: [
      { token: "--green-05", hex: "#f8fbf7" },
      { token: "--green-1", hex: "#e2f4df" },
      { token: "--green-2", hex: "#c5e9bf" },
      { token: "--green-3", hex: "#a7dd9e" },
      { token: "--green-4", hex: "#8ad27e" },
      { token: "--green-5", hex: "#6dc75f" },
      { token: "--green-6", hex: "#579f4b" },
      { token: "--green-7", hex: "#417738" },
      { token: "--green-8", hex: "#2c5026" },
      { token: "--green-9", hex: "#213c1c" }
    ]
  },
  {
    name: "Yellow",
    swatches: [
      { token: "--yellow-1", hex: "#fff5d9" },
      { token: "--yellow-2", hex: "#ffebb2" },
      { token: "--yellow-3", hex: "#ffe18c" },
      { token: "--yellow-4", hex: "#ffd765" },
      { token: "--yellow-5", hex: "#ffcd40" },
      { token: "--yellow-6", hex: "#d0a732" },
      { token: "--yellow-7", hex: "#a28126" },
      { token: "--yellow-8", hex: "#735c19" },
      { token: "--yellow-9", hex: "#5c4913" }
    ]
  },
  {
    name: "Orange",
    swatches: [
      { token: "--orange-1", hex: "#fff2e9" },
      { token: "--orange-2", hex: "#ffd9bd" },
      { token: "--orange-3", hex: "#ffb37b" },
      { token: "--orange-4", hex: "#ff994f" },
      { token: "--orange-5", hex: "#ff8024" },
      { token: "--orange-6", hex: "#cc661c" },
      { token: "--orange-7", hex: "#994d15" },
      { token: "--orange-8", hex: "#66330e" },
      { token: "--orange-9", hex: "#4d260b" }
    ]
  },
  {
    name: "Purple",
    swatches: [
      { token: "--purple-1", hex: "#f1ecff" },
      { token: "--purple-2", hex: "#d5c6ff" },
      { token: "--purple-3", hex: "#c7b3ff" },
      { token: "--purple-4", hex: "#ac8eff" },
      { token: "--purple-5", hex: "#9068ff" },
      { token: "--purple-6", hex: "#7442ff" },
      { token: "--purple-7", hex: "#5e35d1" },
      { token: "--purple-8", hex: "#4828a3" },
      { token: "--purple-9", hex: "#331a75" }
    ]
  },
  {
    name: "Pink",
    swatches: [
      { token: "--pink-1", hex: "#ffeaf9" },
      { token: "--pink-2", hex: "#ffcef2" },
      { token: "--pink-3", hex: "#ffa3e5" },
      { token: "--pink-4", hex: "#ff84dd" },
      { token: "--pink-5", hex: "#ff65d4" },
      { token: "--pink-6", hex: "#d851b2" },
      { token: "--pink-7", hex: "#b13d90" },
      { token: "--pink-8", hex: "#8a286f" },
      { token: "--pink-9", hex: "#771e5e" }
    ]
  }
];

interface ISpacingToken {
  token: string;
  px: number;
}

const GAP_TOKENS: ISpacingToken[] = [
  { token: "--gap-xxs", px: 2 },
  { token: "--gap-xs", px: 4 },
  { token: "--gap-s", px: 8 },
  { token: "--gap-m", px: 12 },
  { token: "--gap-l", px: 16 },
  { token: "--gap-xl", px: 24 },
  { token: "--gap-xxl", px: 32 }
];

interface IShadowToken {
  token: string;
  description: string;
}

const SHADOW_TOKENS: IShadowToken[] = [
  { token: "--shadow-sm", description: "subtle elevation — cards, hover lift" },
  { token: "--shadow-md", description: "popovers and detached panels" },
  { token: "--shadow-sidedrawer", description: "drawer cast, leftward" },
  { token: "--shadow-tooltip", description: "minimal tooltip lift" },
  { token: "--shadow-stylinginput", description: "inset for styling-panel inputs" }
];

const CHART_TOKENS: IColorSwatch[] = [
  { token: "$green-chart", hex: "#86d497" },
  { token: "$yellow-chart", hex: "#ffdf6e" },
  { token: "$blue-chart", hex: "#689ff2" },
  { token: "$red-chart", hex: "#ff6a6a" },
  { token: "$orange-chart", hex: "#ffa462" },
  { token: "$purple-chart", hex: "#ab96ff" },
  { token: "$pink-chart", hex: "#f596da" }
];

interface ISwatchProps {
  token: string;
  hex: string;
}

const Swatch: React.FC<ISwatchProps> = ({ token, hex }: ISwatchProps) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 6,
      width: 120,
      fontFamily: "var(--lab-mono)"
    }}
  >
    <div
      style={{
        width: "100%",
        height: 56,
        background: `var(${token}, ${hex})`,
        border: "1px solid var(--lab-border-strong)"
      }}
    />
    <span style={{ fontSize: 10, color: "var(--lab-ink)", letterSpacing: "0.04em" }}>{token}</span>
    <span style={{ fontSize: 9, color: "var(--lab-sublabel)", textTransform: "uppercase" }}>{hex}</span>
  </div>
);

interface IScaleProps {
  scale: IColorScale;
}

const Scale: React.FC<IScaleProps> = ({ scale }: IScaleProps) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <span className="lab-caption">{scale.name}</span>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
      {scale.swatches.map((sw: IColorSwatch) => (
        <Swatch key={sw.token} token={sw.token} hex={sw.hex} />
      ))}
    </div>
  </div>
);

interface IScaleToken {
  token: string;
  px: string;
  use: string;
}

const RADIUS_TOKENS: IScaleToken[] = [
  { token: "--radius-xs", px: "4px", use: "16–20px controls, chips, tags" },
  { token: "--radius-s", px: "6px", use: "24px controls, tooltips" },
  { token: "--radius-m", px: "8px", use: "28–36px controls, menus, popovers, toasts" },
  { token: "--radius-l", px: "10px", use: "40px+ controls, large cards" },
  { token: "--radius-xl", px: "12px", use: "modals, command palette" },
  { token: "--radius-pill", px: "999px", use: "pills, avatars, status dots" }
];

interface ITokenAlias {
  token: string;
  target: string;
}

const LAYER_RADIUS_ALIASES: ITokenAlias[] = [
  { token: "--radius-tooltip", target: "--radius-s" },
  { token: "--radius-overlay", target: "--radius-m" },
  { token: "--radius-modal", target: "--radius-xl" }
];

type ControlSize = "sm" | "md" | "lg";

interface IControlRung {
  size: ControlSize;
  height: string;
  icon: string;
  padding: string;
  gap: string;
  radius: string;
  radiusPx: string;
  typography: string;
  typeSpec: string;
  // `%typography-control-*` exists only in SCSS, so the sample restates its size and line-height.
  fontSize: number;
  lineHeight: number;
}

const CONTROL_SCALE: IControlRung[] = [
  {
    size: "sm",
    height: "24px",
    icon: "14px",
    padding: "8px",
    gap: "4px",
    radius: "--radius-s",
    radiusPx: "6px",
    typography: "%typography-control-sm",
    typeSpec: "12/16 · 500",
    fontSize: 12,
    lineHeight: 16
  },
  {
    size: "md",
    height: "32px",
    icon: "16px",
    padding: "12px",
    gap: "6px",
    radius: "--radius-m",
    radiusPx: "8px",
    typography: "%typography-control-md",
    typeSpec: "13/18 · 500",
    fontSize: 13,
    lineHeight: 18
  },
  {
    size: "lg",
    height: "40px",
    icon: "18px",
    padding: "16px",
    gap: "8px",
    radius: "--radius-l",
    radiusPx: "10px",
    typography: "%typography-control-lg",
    typeSpec: "14/20 · 500",
    fontSize: 14,
    lineHeight: 20
  }
];

const CONTROL_SCALE_COLUMNS: string[] = [
  "Size",
  "Sample",
  "Measured",
  "Height",
  "Icon",
  "Padding",
  "Gap",
  "Radius",
  "Type"
];

const ROW_TOKENS: IScaleToken[] = [
  { token: "--row-h-compact", px: "28px", use: "dropdown / menu options, tree nodes" },
  { token: "--row-h-default", px: "32px", use: "list rows, select options" },
  { token: "--row-h-comfortable", px: "36px", use: "panel menu rows, collapse headers" },
  { token: "--row-h-table", px: "40px", use: "table rows" }
];

interface IElevationRole {
  token: string;
  radius: string;
  use: string;
}

const ELEVATION_ROLES: IElevationRole[] = [
  { token: "--elevation-overlay", radius: "--radius-overlay", use: "dropdown, popover, toast · frame + --shadow-md" },
  { token: "--elevation-tooltip", radius: "--radius-tooltip", use: "tooltip · frame + --shadow-tooltip" },
  { token: "--elevation-modal", radius: "--radius-modal", use: "modal · frame + --shadow-md, set apart by its scrim" }
];

interface IStackLayer {
  token: string;
  use: string;
}

// Lowest first. The diagram paints them in reverse DOM order, so only z-index can put them right.
const STACK_LAYERS: IStackLayer[] = [
  { token: "--z-sticky", use: "sticky headers" },
  { token: "--z-drawer", use: "side drawers + scrim" },
  { token: "--z-modal", use: "modals + scrim" },
  { token: "--z-overlay", use: "full-screen blocking overlays" },
  { token: "--z-floating", use: "dropdown, select, popover, pickers" },
  { token: "--z-tooltip", use: "tooltips" },
  { token: "--z-toast", use: "toasts" },
  { token: "--z-onboarding", use: "onboarding, reveal tooltips" }
];

const STACK_TOKENS: string[] = STACK_LAYERS.map((layer: IStackLayer): string => layer.token);

const STACK_STEP_X: number = 48;
const STACK_STEP_Y: number = 40;
const STACK_CARD_HEIGHT: number = 56;

// Each token's resolved value off <html>, so the page shows what the stylesheet really says.
// Read once: the stack tokens are theme-independent and never change at runtime.
const readRootTokenValues = (tokens: string[]): Record<string, string> => {
  const rootStyle: CSSStyleDeclaration = getComputedStyle(document.documentElement);
  const resolved: Record<string, string> = {};
  tokens.forEach((token: string): void => {
    resolved[token] = rootStyle.getPropertyValue(token).trim();
  });
  return resolved;
};

const monoLabelStyle: React.CSSProperties = {
  fontFamily: "var(--lab-mono)",
  fontSize: 11,
  color: "var(--lab-ink)"
};

const sublabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: "var(--lab-sublabel)"
};

interface IControlSampleProps {
  rung: IControlRung;
}

const ControlSample: React.FC<IControlSampleProps> = ({ rung }: IControlSampleProps): JSX.Element => {
  const sampleRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

  useLayoutEffect((): void => {
    const sample: HTMLDivElement | null = sampleRef.current;
    if (sample) {
      setMeasuredHeight(sample.getBoundingClientRect().height);
    }
  }, []);

  return (
    <>
      <div
        ref={sampleRef}
        style={{
          boxSizing: "border-box",
          display: "inline-flex",
          alignItems: "center",
          justifySelf: "start",
          height: `var(--control-h-${rung.size})`,
          paddingInline: `var(--control-pad-${rung.size})`,
          gap: `var(--control-gap-${rung.size})`,
          borderRadius: `var(--control-radius-${rung.size})`,
          border: "1px solid var(--control-border)",
          background: "var(--dark-0)",
          color: "var(--dark-11)",
          fontSize: rung.fontSize,
          lineHeight: `${rung.lineHeight}px`,
          fontWeight: 500
        }}
      >
        <span
          style={{
            width: `var(--control-icon-${rung.size})`,
            height: `var(--control-icon-${rung.size})`,
            borderRadius: "var(--radius-xs)",
            background: "var(--lab-accent)"
          }}
        />
        Label
      </div>
      <span style={monoLabelStyle}>{measuredHeight === null ? "—" : `${Math.round(measuredHeight)}px`}</span>
    </>
  );
};

interface IStackedCellProps {
  primary: string;
  secondary: string;
}

const StackedCell: React.FC<IStackedCellProps> = ({ primary, secondary }: IStackedCellProps): JSX.Element => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <span style={monoLabelStyle}>{primary}</span>
    <span style={sublabelStyle}>{secondary}</span>
  </div>
);

const ControlScaleTable: React.FC = (): JSX.Element => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "32px 104px 64px repeat(4, 48px) 96px minmax(180px, 1fr)",
      alignItems: "center",
      columnGap: 12,
      rowGap: 16,
      width: "100%"
    }}
  >
    {CONTROL_SCALE_COLUMNS.map((column: string) => (
      <span key={column} className="lab-caption">
        {column}
      </span>
    ))}
    {CONTROL_SCALE.map((rung: IControlRung) => (
      <React.Fragment key={rung.size}>
        <span style={monoLabelStyle}>{rung.size}</span>
        <ControlSample rung={rung} />
        <span style={monoLabelStyle}>{rung.height}</span>
        <span style={monoLabelStyle}>{rung.icon}</span>
        <span style={monoLabelStyle}>{rung.padding}</span>
        <span style={monoLabelStyle}>{rung.gap}</span>
        <StackedCell primary={rung.radius} secondary={rung.radiusPx} />
        <StackedCell primary={rung.typography} secondary={rung.typeSpec} />
      </React.Fragment>
    ))}
  </div>
);

interface IAliasPairProps {
  token: string;
  caption: string;
  sample: React.ReactNode;
  reference: React.ReactNode;
}

// A token-drawn sample next to the live primitive it must match.
const AliasPair: React.FC<IAliasPairProps> = ({ token, caption, sample, reference }: IAliasPairProps): JSX.Element => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <span style={monoLabelStyle}>{token}</span>
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      {sample}
      <span style={sublabelStyle}>vs</span>
      {reference}
    </div>
    <span style={sublabelStyle}>{caption}</span>
  </div>
);

const StackDiagram: React.FC = (): JSX.Element => {
  const [values] = useState<Record<string, string>>((): Record<string, string> => readRootTokenValues(STACK_TOKENS));
  const lastIndex: number = STACK_LAYERS.length - 1;
  const reversed: IStackLayer[] = [...STACK_LAYERS].reverse();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
      <div
        style={{
          position: "relative",
          isolation: "isolate",
          height: lastIndex * STACK_STEP_Y + STACK_CARD_HEIGHT,
          width: "100%"
        }}
      >
        {reversed.map((layer: IStackLayer, reversedIndex: number) => {
          const index: number = lastIndex - reversedIndex;
          return (
            <div
              key={layer.token}
              style={{
                position: "absolute",
                left: index * STACK_STEP_X,
                top: index * STACK_STEP_Y,
                zIndex: `var(${layer.token})`,
                boxSizing: "border-box",
                width: 280,
                height: STACK_CARD_HEIGHT,
                padding: "6px 10px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                background: "var(--lab-paper)",
                border: "1px solid var(--lab-border-strong)",
                borderRadius: "var(--radius-s)"
              }}
            >
              <span style={monoLabelStyle}>
                {layer.token} · {values[layer.token]}
              </span>
              <span style={sublabelStyle}>{layer.use}</span>
            </div>
          );
        })}
      </div>
      <span style={sublabelStyle}>
        A layer that has to sit above another one takes the rung above it, never a literal. The old --z-dropdown and
        --z-popover are gone; a floating layer uses --z-floating, and the out-of-kit literals still above it are
        migration debt.
      </span>
    </div>
  );
};

export const DesignTokensStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Design Tokens"
      componentName="color-palette.scss · variables.scss · typography.scss · _control.scss"
      sourcePath="styles/base"
      description="The full token inventory — colour scales, neutrals, spacing, the radius ladder, the control scale, row density, elevation and stacking order. Tokens flip automatically between light and dark themes."
    >
      <SpecRow label="Neutrals" caption="--dark-*, --white, --black, --dark-light" align="start">
        <Scale scale={NEUTRAL_SCALE} />
      </SpecRow>
      <SpecRow
        label="Hue scales"
        caption="--blue-* · --red-* · --green-* · --yellow-* · --orange-* · --purple-* · --pink-*"
        align="start"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 24, width: "100%" }}>
          {HUE_SCALES.map((scale: IColorScale) => (
            <Scale key={scale.name} scale={scale} />
          ))}
        </div>
      </SpecRow>
      <SpecRow label="Chart palette" caption="Sass scope — $*-chart" align="start">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {CHART_TOKENS.map((sw: IColorSwatch) => (
            <Swatch key={sw.token} token={sw.token} hex={sw.hex} />
          ))}
        </div>
      </SpecRow>
      <SpecRow label="Spacing — gap" caption="--gap-xxs through --gap-xxl" align="start">
        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
          {GAP_TOKENS.map((token: ISpacingToken) => (
            <div
              key={token.token}
              style={{ display: "flex", alignItems: "center", gap: 16, fontFamily: "var(--lab-mono)", fontSize: 11 }}
            >
              <span style={{ width: 100, color: "var(--lab-ink)" }}>{token.token}</span>
              <span style={{ width: 50, color: "var(--lab-sublabel)" }}>{token.px}px</span>
              <span
                style={{
                  display: "inline-block",
                  height: 14,
                  width: token.px,
                  background: "var(--lab-accent)"
                }}
              />
            </div>
          ))}
        </div>
      </SpecRow>
      <SpecRow
        label="Radius ladder"
        caption="radius follows control height — 16–20 → xs, 24 → s, 28–36 → m, 40+ → l; inner = outer − gap"
        align="start"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 20, width: "100%" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
            {RADIUS_TOKENS.map((token: IScaleToken) => (
              <div key={token.token} style={{ display: "flex", flexDirection: "column", gap: 6, width: 150 }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    background: "var(--lab-paper)",
                    border: "1px solid var(--lab-border-strong)",
                    borderRadius: `var(${token.token})`
                  }}
                />
                <span style={monoLabelStyle}>
                  {token.token} · {token.px}
                </span>
                <span style={sublabelStyle}>{token.use}</span>
              </div>
            ))}
          </div>
          <span style={sublabelStyle}>
            Layers:{" "}
            {LAYER_RADIUS_ALIASES.map((alias: ITokenAlias): string => `${alias.token} → ${alias.target}`).join(" · ")}
          </span>
        </div>
      </SpecRow>
      <SpecRow
        label="Control scale"
        caption="--control-h/-icon/-pad/-gap/-radius-{sm,md,lg} · @include kuika-control-size() in _control.scss"
        align="start"
      >
        <ControlScaleTable />
      </SpecRow>
      <SpecRow label="Row density" caption="--row-h-compact · -default · -comfortable · -table" align="start">
        <div style={{ display: "flex", flexDirection: "column", width: "100%", maxWidth: 520 }}>
          {ROW_TOKENS.map((token: IScaleToken) => (
            <div
              key={token.token}
              style={{
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                gap: 16,
                height: `var(${token.token})`,
                paddingInline: 8,
                borderBottom: "var(--border)"
              }}
            >
              <span style={{ ...monoLabelStyle, width: 160 }}>{token.token}</span>
              <span style={{ ...sublabelStyle, width: 40 }}>{token.px}</span>
              <span style={sublabelStyle}>{token.use}</span>
            </div>
          ))}
        </div>
      </SpecRow>
      <SpecRow label="Hit area · disabled" caption="--hit-min 32px · --disabled-opacity 0.5" align="start">
        <div style={{ display: "flex", alignItems: "center", gap: 40 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                boxSizing: "border-box",
                display: "grid",
                placeItems: "center",
                width: "var(--hit-min)",
                height: "var(--hit-min)",
                border: "1px dashed var(--lab-accent)"
              }}
            >
              <span
                style={{ width: 16, height: 16, borderRadius: "var(--radius-xs)", background: "var(--lab-accent)" }}
              />
            </div>
            <span style={sublabelStyle}>16px glyph, --hit-min clickable</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ ...monoLabelStyle, padding: "4px 8px", border: "var(--border)" }}>Enabled</span>
            <span
              style={{
                ...monoLabelStyle,
                padding: "4px 8px",
                border: "var(--border)",
                opacity: "var(--disabled-opacity)",
                cursor: "not-allowed"
              }}
            >
              Disabled
            </span>
          </div>
        </div>
      </SpecRow>
      <SpecRow
        label="Control edges"
        caption="aliases of today's values — each sample must match the live primitive beside it"
        align="start"
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 40 }}>
          <AliasPair
            token="--control-border · --control-placeholder"
            caption="--dark-3 border · --dark-7 placeholder (--dark-5 in dark)"
            sample={
              <div
                style={{
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  width: 180,
                  height: 32,
                  padding: "0 8px",
                  border: "1px solid var(--control-border)",
                  borderRadius: "var(--radius-xs)",
                  background: "var(--dark-0)",
                  color: "var(--control-placeholder)",
                  fontSize: 14,
                  lineHeight: "20px"
                }}
              >
                Placeholder
              </div>
            }
            reference={
              <div style={{ width: 180 }}>
                <KuikaInput size="md" placeholder="Placeholder" />
              </div>
            }
          />
          <AliasPair
            token="--control-glyph-border"
            caption="--dark-4 — the checkbox / radio ring"
            sample={
              <span
                style={{
                  boxSizing: "border-box",
                  width: 16,
                  height: 16,
                  border: "1px solid var(--control-glyph-border)",
                  borderRadius: "var(--radius-xs)",
                  background: "var(--dark-0)"
                }}
              />
            }
            reference={<KuikaCheckbox checked={false} onChange={noop} dataTestId="design-tokens-glyph-checkbox" />}
          />
        </div>
      </SpecRow>
      <SpecRow label="Elevation — shadow" caption="five shadow tokens with dark-theme variants" align="start">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 24,
            width: "100%"
          }}
        >
          {SHADOW_TOKENS.map((token: IShadowToken) => (
            <div key={token.token} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div
                style={{
                  height: 80,
                  background: "var(--lab-paper)",
                  border: "1px solid var(--lab-border)",
                  boxShadow: `var(${token.token})`
                }}
              />
              <span style={{ fontFamily: "var(--lab-mono)", fontSize: 11, color: "var(--lab-ink)" }}>
                {token.token}
              </span>
              <span style={{ fontSize: 11, color: "var(--lab-sublabel)" }}>{token.description}</span>
            </div>
          ))}
        </div>
      </SpecRow>
      <SpecRow
        label="Elevation roles"
        caption="--elevation-overlay · -tooltip · -modal, each with --shadow-frame"
        align="start"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 24,
            width: "100%"
          }}
        >
          {ELEVATION_ROLES.map((role: IElevationRole) => (
            <div key={role.token} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div
                style={{
                  height: 80,
                  background: "var(--lab-paper)",
                  borderRadius: `var(${role.radius})`,
                  boxShadow: `var(${role.token})`
                }}
              />
              <span style={monoLabelStyle}>{role.token}</span>
              <span style={sublabelStyle}>
                {role.radius} · {role.use}
              </span>
            </div>
          ))}
        </div>
      </SpecRow>
      <SpecRow label="Border" caption="--border (--dark-3 in both themes)" align="start">
        <div
          style={{
            width: 240,
            height: 64,
            border: "var(--border)",
            display: "grid",
            placeItems: "center",
            fontFamily: "var(--lab-mono)",
            fontSize: 11,
            color: "var(--lab-ink-muted)"
          }}
        >
          1px solid var(--dark-3)
        </div>
      </SpecRow>
      <SpecRow
        label="Stacking order"
        caption="sticky < drawer < modal < floating < tooltip < toast < onboarding · live values"
        align="start"
      >
        <StackDiagram />
      </SpecRow>
    </ShowcaseFrame>
  );
};
